/**
 * trajectory.ts — Trajectory computation from f(x)
 *
 * Samples the parsed function to generate a path of points
 * that the projectile follows from the player's position.
 */

/** A single point on the trajectory */
export interface TrajectoryPoint {
  x: number;
  y: number;
}

/** Bounds of the game field */
export interface FieldBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Default game field bounds (GraphWar-style) */
export const DEFAULT_FIELD_BOUNDS: FieldBounds = {
  minX: -25,
  maxX: 25,
  minY: -20,
  maxY: 20,
};

export interface CoordMapper {
  bounds: FieldBounds;
  toScreenX: (wx: number) => number;
  toScreenY: (wy: number) => number;
  toWorldX: (sx: number) => number;
  toWorldY: (sy: number) => number;
}

export function createCoordMapper(
  width: number,
  height: number,
  inputBounds: FieldBounds = DEFAULT_FIELD_BOUNDS,
  margin = { top: 70, bottom: 100, left: 60, right: 40 }
): CoordMapper {
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  // Dynamically calculate vertical bounds to ensure uniform aspect ratio (square grid)
  const worldWidth = inputBounds.maxX - inputBounds.minX;
  const worldHeight = worldWidth * (plotHeight / plotWidth);
  
  const bounds = {
    ...inputBounds,
    minY: -worldHeight / 2,
    maxY: worldHeight / 2,
  };

  return {
    bounds,
    toScreenX: (wx: number) =>
      margin.left + ((wx - bounds.minX) / (bounds.maxX - bounds.minX)) * plotWidth,
    toScreenY: (wy: number) =>
      margin.top + ((bounds.maxY - wy) / (bounds.maxY - bounds.minY)) * plotHeight,
    toWorldX: (sx: number) =>
      bounds.minX + ((sx - margin.left) / plotWidth) * (bounds.maxX - bounds.minX),
    toWorldY: (sy: number) =>
      bounds.maxY - ((sy - margin.top) / plotHeight) * (bounds.maxY - bounds.minY),
  };
}

/**
 * Computes the trajectory points for a given function.
 *
 * The function is offset so it passes through the player's position:
 *   y = f(x - playerX) + playerY
 *
 * The trajectory is sampled from the player's X position outward
 * in the specified direction until it leaves the field bounds.
 *
 * @param fn - The compiled evaluator (x => number)
 * @param playerX - Player's X position on the field
 * @param playerY - Player's Y position on the field
 * @param direction - 1 for rightward, -1 for leftward
 * @param bounds - Field boundaries
 * @param step - Sampling step size (smaller = smoother, default 0.05)
 * @returns Array of trajectory points
 */
export function computeTrajectory(
  fn: (x: number) => number,
  playerX: number,
  playerY: number,
  direction: 1 | -1,
  bounds: FieldBounds = DEFAULT_FIELD_BOUNDS,
  step: number = 0.05,
): TrajectoryPoint[] {
  const points: TrajectoryPoint[] = [];

  // Evaluate f(0) to offset curve so it always originates at player position (0, 0)
  const f0Raw = fn(0);
  const f0 = isNaN(f0Raw) || !isFinite(f0Raw) ? 0 : f0Raw;

  // Start at player position
  const startX = playerX;
  const endX = direction === 1 ? bounds.maxX : bounds.minX;
  const totalSteps = Math.ceil(Math.abs(endX - startX) / step);

  for (let i = 0; i <= totalSteps; i++) {
    const worldX = startX + i * step * direction;

    // Evaluate f(localX) - f(0) + playerY so curve always passes through player position
    const localX = (worldX - playerX) * direction; // always positive going forward
    const rawY = fn(localX) - f0;

    if (isNaN(rawY) || !isFinite(rawY)) {
      // Skip NaN/Infinity points but continue — the function might recover
      continue;
    }

    const worldY = rawY + playerY;

    // Check bounds
    if (worldX < bounds.minX || worldX > bounds.maxX) break;
    if (worldY < bounds.minY || worldY > bounds.maxY) {
      // Out of vertical bounds — add a clamped point and stop
      points.push({
        x: worldX,
        y: Math.max(bounds.minY, Math.min(bounds.maxY, worldY)),
      });
      break;
    }

    points.push({ x: worldX, y: worldY });
  }

  return points;
}

/**
 * Calculates square of closest distance from point C to line segment AB.
 */
function closestDistanceToSegmentSq(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    const dX = cx - ax;
    const dY = cy - ay;
    return dX * dX + dY * dY;
  }

  let t = ((cx - ax) * dx + (cy - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const projX = ax + t * dx;
  const projY = ay + t * dy;
  const dX = cx - projX;
  const dY = cy - projY;
  return dX * dX + dY * dY;
}

/**
 * Checks if a trajectory passes close enough to a target position
 * to count as a hit using continuous line-segment collision.
 *
 * @param trajectory - Array of trajectory points
 * @param targetX - Target's X position
 * @param targetY - Target's Y position
 * @param hitRadius - Radius for collision detection (default 0.55)
 * @returns The index of the first hitting segment, or -1 if no hit
 */
export function checkHit(
  trajectory: TrajectoryPoint[],
  targetX: number,
  targetY: number,
  hitRadius: number = 0.55,
): number {
  if (trajectory.length === 0) return -1;
  if (trajectory.length === 1) {
    const dx = trajectory[0].x - targetX;
    const dy = trajectory[0].y - targetY;
    return dx * dx + dy * dy <= hitRadius * hitRadius ? 0 : -1;
  }

  const radiusSq = hitRadius * hitRadius;

  for (let i = 0; i < trajectory.length - 1; i++) {
    const p1 = trajectory[i];
    const p2 = trajectory[i + 1];

    const distSq = closestDistanceToSegmentSq(p1.x, p1.y, p2.x, p2.y, targetX, targetY);
    if (distSq <= radiusSq) {
      return i;
    }
  }

  return -1;
}
